import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// components and helpers
import * as dashboardActions from '../Dashboard/DashboardActions';
import {
    activityQueries,
    caseQueries,
    fileQueries,
    metricsQueries,
} from '../helpers/queries';
import DashboardMetrics from './DashboardMetrics';
import totalCasesIcon from '../../../assets/img/TotalCasesIcon.svg';
import importsIcon from '../../../assets/img/Group 567.svg';
import MUIDataTable from 'mui-datatables';
import utils from '../../../utils/utils';
import ActivityIcon2 from '../../../assets/img/ActivityIcon2.svg';
import Counts from '../../Shared/Counts';
import * as caseActions from '../Case/CaseActions';
// mui
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import Divider from '@mui/material/Divider';

const MetricsDashboard: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const dashboard = useSelector((state: any) => state.dashboard);
    const [sortField] = useState<string>('registrationDate');
    const [sortDirection] = useState<string>('DESC');
    const [pageNumber] = useState<number>(0);
    const [year, setYear] = useState<string>('');

    useEffect(() => {
        if (
            dashboard &&
            dashboard.caseList &&
            dashboard.caseList.latestCase &&
            dashboard.caseList.latestCase.items &&
            !year
        ) {
            const workspaceId = location.pathname.split('/').pop();
            if (dashboard.caseList.latestCase.items.length) {
                const latestYear = moment(
                    dashboard.caseList.latestCase.items[0].registrationDate
                ).format('YYYY');
                setYear(latestYear);
                dispatch(
                    dashboardActions?.getDashboardDataMultiple(
                        'CASE',
                        workspaceId,
                        metricsQueries(workspaceId, Number(latestYear)),
                        true
                    )
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard, year]);

    useEffect(() => {
        const workspaceId = location.pathname.split('/').pop();
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'CASE',
                workspaceId,
                caseQueries
            )
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'FILE',
                workspaceId,
                fileQueries
            )
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'ACTIVITY',
                workspaceId,
                activityQueries
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenCase = useCallback((data: any) => {
        const workspaceId = location.pathname.split('/').pop();
        let id = data[0];
        if (id.includes('/')) {
            id = data[0].split('/')[1];
        }

        navigate(`${location.pathname}?id=analytics`);

        queueMicrotask(() => {
            dispatch(caseActions?.getCase(workspaceId, id));
            navigate(`${location.pathname}?caseId=${id}`);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const counts = [
        {
            icon: <img src={totalCasesIcon} alt="" width={40} />,
            title: 'Workspace Cases',
            color: 'linear-gradient(to right, #663299 40%, #66329999) !important',
            data:
                (dashboard &&
                    dashboard.caseList &&
                    dashboard.caseList.casesReportCounts &&
                    dashboard.caseList.casesReportCounts.items &&
                    dashboard.caseList.casesReportCounts.items[0] &&
                    dashboard.caseList.casesReportCounts.items[0].COUNT &&
                    dashboard.caseList.casesReportCounts.items[0].COUNT.toLocaleString()) ||
                0,
        },
        {
            icon: <img src={importsIcon} alt="" width={40} />,
            title: 'Workspace Imports',
            color: 'linear-gradient(to right, #325599 40%, #32559999) !important',
            data:
                (dashboard &&
                    dashboard.fileCount &&
                    dashboard.fileCount.importReportCounts &&
                    dashboard.fileCount.importReportCounts.items &&
                    dashboard.fileCount.importReportCounts.items[0] &&
                    dashboard.fileCount.importReportCounts.items[0].COUNT &&
                    dashboard.fileCount.importReportCounts.items[0].COUNT.toLocaleString()) ||
                0,
        },
        {
            icon: <img src={ActivityIcon2} alt="" width={50} />,
            title: 'Workspace Activities',
            color: 'linear-gradient(to right, #008577 40%, #00857799) !important',
            data:
                (dashboard &&
                    dashboard.activityCount &&
                    dashboard.activityCount.items &&
                    dashboard.activityCount.items[0] &&
                    dashboard.activityCount.items[0].COUNT &&
                    dashboard.activityCount.items[0].COUNT.toLocaleString()) ||
                0,
        },
    ];

    const columns = [
        {
            name: '_id',
            label: 'Case ID',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        if (value.includes('/')) {
                            return value.split('/').pop();
                        } else {
                            return value;
                        }
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'registrationDate',
            label: 'Registration Date',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return utils.formatDate(value, 'L');
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'physician',
            label: 'Physician',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'status',
            label: 'Status',
            options: {
                customBodyRender: (value: string) => {
                    if (value) {
                        return value;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'diagnosticsAwaitingTat',
            label: 'Diagnostic Awaiting TAT',
            options: {
                customBodyRender: (value: string) => {
                    // @ts-ignore
                    if (value || value === 0) {
                        return `${value} day(s)`;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'diagnosticsTat',
            label: 'Diagnostic TAT',
            options: {
                customBodyRender: (value: string) => {
                    // @ts-ignore
                    if (value || value === 0) {
                        return `${value} day(s)`;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'TreatmentPlanningTat',
            label: 'Treatment Planning TAT',
            options: {
                customBodyRender: (value: string) => {
                    // @ts-ignore
                    if (value || value === 0) {
                        return `${value} day(s)`;
                    } else {
                        return '---';
                    }
                },
            },
        },
        {
            name: 'TreatmentAwaitingTat',
            label: 'Treatment Awaiting TAT',
            options: {
                customBodyRender: (value: string) => {
                    // @ts-ignore
                    if (value || value === 0) {
                        return `${value} day(s)`;
                    } else {
                        return '---';
                    }
                },
            },
        },
    ];

    const options = {
        elevation: 0,
        search: false,
        filter: false,
        download: false,
        print: false,
        onRowClick: handleOpenCase,
        // onTableChange: handleTableChange,
        count:
            (dashboard &&
                dashboard.caseList &&
                dashboard.caseList.recentCases &&
                dashboard.caseList.recentCases.items &&
                dashboard.caseList.recentCases.items.length) ||
            0,
        page: pageNumber,
        serverSide: false,
        rowsPerPage: 5,
        rowsPerPageOptions: [5],
        sortOrder: {
            // @ts-ignore
            direction: sortDirection.toLowerCase() as 'desc' | 'asc',
            name: sortField as string,
        },
        viewColumns: false,
        selectableRowsHeader: false,
        selectableRowsHideCheckboxes: true,
        tableBodyHeight: '210px',
        textLabels: {
            body: {
                noMatch: 'No recent cases found.',
            },
        },
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Grid container spacing={3} direction={'row'}>
                    {counts.map((count, i: number) => {
                        return <Counts count={count} index={i} />;
                    })}
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: '0px 16px 0px 16px' }}>
                    <MUIDataTable
                        data={
                            dashboard &&
                            dashboard.caseList &&
                            dashboard.caseList.recentCases &&
                            dashboard.caseList.recentCases.items
                        }
                        columns={columns}
                        title={
                            <Typography variant={'h6'}>
                                Latest Cases (Last 90 Days)
                            </Typography>
                        }
                        options={options}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12} sx={{ mb: 3 }}>
                <Paper elevation={0} sx={{ p: '16px' }}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography
                                variant={'h6'}
                                sx={{ fontWeight: 'bold' }}
                            >
                                Today's Case Metrics
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant={'body2'}>
                                {
                                    new Date(Date.now())
                                        .toLocaleString()
                                        .split(',')[0]
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ pt: 2, pb: 2 }}>
                            <Divider />
                        </Grid>
                        <Grid item xs={12}>
                            <DashboardMetrics />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default MetricsDashboard;
